import { useQuery } from '@tanstack/react-query';
import {
  CertificationEquipmentLink,
  CertificationEquipmentLinksQuery,
  CertificationEquipmentLinksQueryVariables,
  SortOrder,
} from '__generated__/graphql';
import { CERTIFICATION_FRAGMENT } from 'apollo/fragments/certification-fragments';
import gql from 'graphql-tag';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { logError } from 'utils/testUtils';

interface IUseCertificationEquipmentLinksProps {
  equipmentId: string;
}

export function useCertificationEquipmentLinks({ equipmentId }: IUseCertificationEquipmentLinksProps) {
  const graphqlClient = useGraphQLClient();
  const baseQueryKey = useReactQueryBaseKey();

  const variables: CertificationEquipmentLinksQueryVariables = {
    filter: {
      equipmentId: { equal: equipmentId },
      deletedOn: { isNull: true },
    },
    sort: [{ certification: { name: SortOrder.Asc } }],
  };

  async function getLinks(
    after: string | null,
    first: number
  ): Promise<
    Array<Pick<CertificationEquipmentLink, 'id' | 'certificationId' | 'equipmentId' | 'cursor' | 'certification'>>
  > {
    const pagedVariables: CertificationEquipmentLinksQueryVariables = {
      after,
      first,
      ...variables,
    };

    const result = await graphqlClient
      .request<CertificationEquipmentLinksQuery, CertificationEquipmentLinksQueryVariables>({
        document: CERTIFICATION_EQUIPMENT_LINKS_QUERY,
        variables: pagedVariables,
      })
      .catch(logError);

    return result.certificationEquipmentLinks ?? [];
  }

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [...baseQueryKey, 'equipment-certifications-list', variables],
    queryFn: () => getLinks(null, 500)
  });

  return { data, isLoading, isError, refetch };
}

export const CERTIFICATION_EQUIPMENT_LINK_FRAGMENT = gql`
  fragment CertificationEquipmentLinkFragment on CertificationEquipmentLink {
    id
    certificationId
    equipmentId
    deletedOn
    certification {
      organizationId
      ...CertificationFragment
    }
  }
  ${CERTIFICATION_FRAGMENT}
`;

export const CERTIFICATION_EQUIPMENT_LINKS_QUERY = gql`
  query CertificationEquipmentLinks(
    $after: String
    $first: Int
    $filter: CertificationEquipmentLinkFilter
    $sort: [CertificationEquipmentLinkSort!]
  ) {
    certificationEquipmentLinks(after: $after, first: $first, filter: $filter, sort: $sort) {
      id
      certificationId
      equipmentId
      deletedOn
      cursor
      certification {
        organizationId
        cursor
        ...CertificationFragment
      }
    }
  }
  ${CERTIFICATION_FRAGMENT}
`;
